import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ClipMetadataService from '../services/clipMetadataService';
import ViralClipsMetadata from '../components/ViralClipsMetadata';
import './css/EnhancedClipsMetadataPage.css';

const EnhancedClipsMetadataPage = () => {
  const { videoId, chunkId = '0' } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clipsData, setClipsData] = useState(null);
  const [availableChunks, setAvailableChunks] = useState([]);
  const [selectedChunkId, setSelectedChunkId] = useState(chunkId || '0');

  // Fetch the enhanced clips metadata for the specified video and chunk
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!videoId) {
        setError('No video ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch list of available chunks for this video
        const chunksData = await ClipMetadataService.getVideoEnhancedClipsMetadata(videoId);
        setAvailableChunks(chunksData.chunkIds || []);
        
        // Determine which chunk to load
        const chunkToLoad = selectedChunkId || chunksData.chunkIds?.[0] || '0';
        
        // Fetch the enhanced clips metadata for the video and chunk
        const metadata = await ClipMetadataService.getEnhancedClipsMetadata(videoId, chunkToLoad);
        
        // Update the state with the fetched data
        setClipsData(metadata);
        setLoading(false);
      } catch (err) {
        console.error(`Error fetching enhanced clips metadata for ${videoId}/${selectedChunkId}:`, err);
        setError(err.message || 'Failed to fetch enhanced clips metadata');
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [videoId, selectedChunkId]);

  // Handle chunk selection
  const handleChunkSelect = (e) => {
    const newChunkId = e.target.value;
    setSelectedChunkId(newChunkId);
    navigate(`/enhanced-clips/${videoId}/${newChunkId}`);
  };

  return (
    <div className="enhanced-clips-metadata-page">
      <div className="page-header">
        <h1>Enhanced Clips Metadata</h1>
        
        <div className="video-info">
          <div className="video-id">
            <span className="label">Video ID:</span>
            <span className="value">{videoId}</span>
          </div>
          
          {availableChunks.length > 0 && (
            <div className="chunk-selector">
              <label htmlFor="chunk-select">Chunk:</label>
              <select 
                id="chunk-select" 
                value={selectedChunkId} 
                onChange={handleChunkSelect}
              >
                {availableChunks.map((id) => (
                  <option key={id} value={id}>
                    Chunk {id}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading enhanced clips metadata...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      ) : clipsData ? (
        <ViralClipsMetadata clipsData={clipsData} isEnhanced={true} />
      ) : (
        <div className="no-data">
          <p>No enhanced clips metadata available for this video.</p>
        </div>
      )}
    </div>
  );
};

export default EnhancedClipsMetadataPage; 